import { DateTime, IANAZone } from 'luxon';

/**
 * Time-zone helpers. Everything is based on IANA zone names ("Asia/Beirut",
 * "Europe/London") resolved through the runtime's tz database. Fixed UTC
 * offsets are deliberately rejected — they break on daylight-saving changes.
 */

const FIXED_OFFSET = /^(UTC|GMT)?\s*[+-]\d{1,2}(:?\d{2})?$/i;

export function isValidTimeZone(zone: string): boolean {
  if (!zone || typeof zone !== 'string') return false;
  if (FIXED_OFFSET.test(zone.trim())) return false;
  if (/^Etc\/GMT[+-]/i.test(zone)) return false; // fixed-offset aliases
  return IANAZone.isValidZone(zone);
}

/** All IANA zones known to the runtime, sorted. */
export function listTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  const zones = intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  const set = new Set(zones.filter((z) => isValidTimeZone(z)));
  set.add('UTC');
  return [...set].sort();
}

/** Current UTC offset label for a zone, e.g. "UTC+03:00" (for display only). */
export function offsetLabel(zone: string, at: Date = new Date()): string {
  const dt = DateTime.fromJSDate(at).setZone(zone);
  return `UTC${dt.toFormat('ZZ')}`;
}

/** Calendar date (YYYY-MM-DD) of an instant in a given zone. */
export function localDateOf(instant: Date | string, zone: string): string {
  const dt =
    typeof instant === 'string' ? DateTime.fromISO(instant, { zone: 'utc' }) : DateTime.fromJSDate(instant);
  return dt.setZone(zone).toISODate()!;
}

/**
 * Convert a local wall-clock date+time in a zone to a UTC instant.
 * For times that do not exist (spring-forward gap) Luxon moves forward;
 * for ambiguous times (fall-back) the earlier offset is used.
 */
export function zonedToUtc(date: string, time: string, zone: string): Date {
  const dt = DateTime.fromISO(`${date}T${time}`, { zone });
  if (!dt.isValid) throw new Error(`Invalid local date/time ${date} ${time} in ${zone}`);
  return dt.toUTC().toJSDate();
}

/** Format an instant for display in a zone. */
export function formatInZone(
  instant: Date | string | null | undefined,
  zone: string,
  format = 'dd LLL yyyy, HH:mm',
): string {
  if (!instant) return '—';
  const dt =
    typeof instant === 'string' ? DateTime.fromISO(instant, { zone: 'utc' }) : DateTime.fromJSDate(instant);
  if (!dt.isValid) return '—';
  return dt.setZone(zone).toFormat(format);
}

/** Minutes → "7h 05m". */
export function formatMinutes(total: number | null | undefined): string {
  if (total == null || Number.isNaN(total)) return '—';
  const sign = total < 0 ? '−' : '';
  const abs = Math.abs(Math.round(total));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${sign}${m}m`;
  return `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}

/** "HH:MM" or "HH:MM:SS" → minutes after midnight. */
export function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Today's date in a zone. */
export function todayIn(zone: string, now: Date = new Date()): string {
  return DateTime.fromJSDate(now).setZone(zone).toISODate()!;
}
